import { Action } from "redux";

import { ReduxCompatibleValue, TypedObject } from "@multiweb/types/react";
import {
    CLEAR_CONTROLS_VALUE,
    CLEAR_VALIDATION_ERRORS,
    CONTROLS_VALUE_CHANGED,
    INIT_CONTROLS_VALUE,
    SET_VALIDATION_DESCRIPTIONS,
    VALIDATE,
} from "./constants";
import { IExplicitValidationError, IExplicitValidationFnDescription, IInstantValidationFnDescription } from "./validation";

/**
 * Klíč pro jednoznačnou identifikaci stavu pole. Na základě jednoho klíče je možné přistupovat ke stavu z více komponent.
 */
export type ControlsKey = string;

/**
 * Režim instantních validací při zadávání nové hodnoty do Reduxu. (Typicky akce {@link IControlsValueChangedAction}.)
 */
export enum ControlsValueInstantValidationMode {
    /**
     * Režim validací používaný při editaci člověkem.
     * - Je žádoucí, aby člověk mohl zadat pouze validní hodnoty - s ohledem na instantní validace.
     * - Musí se ale také ošetřit stav, kdy již v Reduxu je nevalidní hodnota.
     *
     * Takže:
     * - Pokud stávající hodnota v Reduxu je validní (s ohledem na instantní validace), pak i nová hodnota musí být validní. Jinak se do Reduxu nepropíše.
     * - Pokud stávající hodnota validní není, pak se nová hodnota 1:1 propíše bez validací.
     */
    userEdit = "userEdit",
    /**
     * Jde o iniciální hodnotu - typicky získanou ze serveru. (Tj. Při načtení dat ze serveru do formuláře.)
     * Tato hodnota musí být do Reduxu propsána 1:1, i kdyby byla neplatná.
     *
     * -
     *
     * Pozn. k explicitním validacím:
     * - `errors` reducer musí zohlednit i fakt, že hodnota není validní s ohledem na instantní validace.
     * - Ale pouze, pokud se aktuální hodnota změnila od té iniciální. (Typicky proto, že ji změnil uživatel přes `userEdit`.)
     */
    initialValue = "initialValue",
    /**
     * Zadanou hodnotu 1:1 uloží do Reduxu. I kdyby byla nevalidní.
     * Přitom nejde o iniciální hodnotu.
     *
     * -
     *
     * Používá se v setteru `EditableControlConnector.controlValue`.
     * - Ten by se neměl používat při běžné editaci uživatelem. Ale pouze pro explicitní programové nastavení.
     * - Voláno např. v `DetailFrame` při změně hodnoty pro nastavení původní - nevalidní - hodnoty.
     */
    allowInvalid = "allowInvalid",
}

/**
 * Struktura stavu modulu `controls`.
 */
export interface IControlsState {
    readonly controlsInstances: ControlsInstances;
}

/**
 * Hodnoty jednotlivých polí indexovaných podle klíčů.
 */
export type ControlsInstances = TypedObject<ControlsInstance>;

/**
 * Interní hodnota controlu představující jeho vnitřní stav - i nevalidní.
 * - Např. pro `DateEdit.tsx` tu může být i `"xoxoxo"`, když si to tam uživatel zapíše.
 * - Tato hodnota je interní záležitostí každého control. (Např. pro `ComboBox` to může být třeba index položky nebo zobrazený text uživateli. Dle toho, co je interně výhodnější na implementaci.)
 * - Je zodpovědností každého control, jak `rawValue` vyrenderuje. (Např. `CheckBox` bude mít asi `rawValue: boolean` a vyrenderuje to jako `<input type='checkbox' value={rawValue} />`.)
 * - Hodnota se typicky ukládá do Reduxu. Proto se do ní smí ukládat pouze typy vhodné pro Redux. Viz {@link ReduxCompatibleValue}.
 * - Hodnota se používá i navenek controlu. Ale pouze jako _"zapouzdřený black box"_.
 *
 * **Pozn. k vnějšímu použití jako blackboxu**:
 * - Dá se využít např. k uchování stávající hodnoty za účelem jejího pozdějšího obnovení či distribuci do jiných komponent (téhož typu).
 * ```
 *   const rawValueAsBlackBox = someComponent.rawValue;
 *   ...
 *   anotherComponent.rawValue = rawValueAsBlackBox;
 * ```
 *
 * **Pozn2. Z historických důvodů se této hodnotě také někdy říká `displayedValue`**.
 * - V původní/nereactí appce se tato hodnota typicky 1:1 předávala do HTML elementu `<input value=rawValue>`.
 * - A to se typicky 1:1 zobrazilo uživateli.
 * - Pří přepisu do Reactu se ukázalo výhodnější/obecnější tomu říkat `rawValue`.
 * - Resp. Je to trochu komplikovanější, protože `IAttributeControl.displayedValue` slouží pro dva různé usecases. Více viz `EditableControlConnector.displayedValue`.
 */
export type ControlReduxRawValue = ReduxCompatibleValue;

/**
 * Struktura dat pro jedno pole. <br>
 * K datům může v jeden moment přistupovat více komponent. Tento počet je udržován v proměnné `counter`.<br>
 * Field `rawValue` reprezentuje hodnotu stavu pole. <br>
 */
export type ControlsInstance = Readonly<{
    /** Aktuální hodnota pole. */
    readonly rawValue: ControlReduxRawValue;
    /** Původní hodnota pole - nastavená při režimu `ControlsValueInstantValidationMode.initialValue`. */
    readonly initialRawValue: ControlReduxRawValue;
    readonly instantValidationFnDescriptions: ReadonlyArray<IInstantValidationFnDescription>;
    readonly explicitValidationFnDescriptions: ReadonlyArray<IExplicitValidationFnDescription>;
    readonly errors: ReadonlyArray<IExplicitValidationError>;
    readonly counter: number;
}>;

/**
 * Extra parametry pro reducer `errors`.
 * Jde o nové hodnoty některých dalších reducerů.
 */
export type ErrorsReducerExtraParams = Pick<ControlsInstance, "rawValue" | "initialRawValue" | "instantValidationFnDescriptions" | "explicitValidationFnDescriptions">;

/**
 * Parametry selektoru, který vrací {@link createStableGetMergedValidationErrors}.
 */
export type MergeValidationErrorsParams = {
    dataKey: ControlsKey;
    /** Chyby evidované (k téže komponentě) mimo redux (v původním nereactím MultiWebu). */
    externalErrors: ReadonlyArray<IExplicitValidationError>;
};

export interface IControlsMappedAction extends Action<string> {
    readonly meta: {
        readonly controlsKey: ControlsKey;
    };
}

export class IControlsMappedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance?.meta?.controlsKey;
    }
}

export interface IInitControlsValueAction extends IControlsMappedAction {
    readonly payload: {
        readonly rawValue: ControlReduxRawValue;
    };
}

export class IInitControlsValueAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === INIT_CONTROLS_VALUE;
    }
}

export interface ISetValidationDescriptionsAction extends IControlsMappedAction {
    readonly payload: {
        readonly instantValidationFnDescriptions: ReadonlyArray<IInstantValidationFnDescription>;
        readonly explicitValidationFnDescriptions: ReadonlyArray<IExplicitValidationFnDescription>;
    };
}

export class ISetValidationDescriptionsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_VALIDATION_DESCRIPTIONS;
    }
}

export interface IControlsValueChangedAction extends IControlsMappedAction {
    readonly payload: {
        readonly rawValue: ControlReduxRawValue;
        readonly validationMode: ControlsValueInstantValidationMode;
    };
}

export class IControlsValueChangedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === CONTROLS_VALUE_CHANGED;
    }
}

export interface IClearControlsValueAction extends IControlsMappedAction {}

export class IClearControlsValueAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === CLEAR_CONTROLS_VALUE;
    }
}

export interface IValidateAction extends IControlsMappedAction {}

export class IValidateAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === VALIDATE;
    }
}

export interface IClearValidationErrorsAction extends IControlsMappedAction {}

export class IClearValidationErrorsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === CLEAR_VALIDATION_ERRORS;
    }
}
